'use client';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _extends from "@babel/runtime/helpers/esm/extends";
const _excluded = ["occurrence", "variant", "className"];
import * as React from 'react';
import clsx from 'clsx';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import RepeatRounded from '@mui/icons-material/RepeatRounded';
import { CalendarGrid } from '@mui/x-scheduler-internals/calendar-grid';
import { EventDragPreview } from "../../event-drag-preview/index.mjs";
import { useFormatTime } from "../../../hooks/useFormatTime.mjs";
import { useEventCalendarStyledContext } from "../../../../event-calendar/EventCalendarStyledContext.mjs";
import { useTimeGridEvent } from "./useTimeGridEvent.mjs";
import { getTimeGridEventRootStyles, linesClampStyles, TimeGridEventPlaceholder } from "./TimeGridEventShared.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const TimeGridEventRoot = styled(CalendarGrid.TimeEvent, {
  name: 'MuiEventCalendar',
  slot: 'TimeGridEventRoot'
})(({
  theme
}) => _extends({}, getTimeGridEventRootStyles(theme), {
  padding: theme.spacing(0.5, 1, 0.5, 1),
  '&[data-under-fifteen-minutes="true"]': {
    padding: theme.spacing(0, 1)
  },
  '&:hover': {
    backgroundColor: 'var(--event-surface-subtle-hover)'
  },
  '&[data-editing]': {
    backgroundColor: 'var(--event-surface-selected)',
    color: 'var(--event-on-surface-selected)',
    '&:hover': {
      backgroundColor: 'var(--event-surface-selected-hover)'
    }
  },
  '&[role="button"]': {
    cursor: 'pointer'
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    width: 3,
    borderRadius: '4px 0 0 4px',
    background: 'var(--event-surface-accent)',
    pointerEvents: 'none'
  },
  '&[data-editing]::before': {
    background: 'var(--event-surface-selected)'
  }
}));
const TimeGridEventTitle = styled(Typography, {
  name: 'MuiEventCalendar',
  slot: 'TimeGridEventTitle'
})(({
  theme
}) => _extends({
  margin: 0,
  color: 'var(--event-on-surface-subtle-primary)',
  fontWeight: theme.typography.fontWeightMedium,
  fontSize: 'var(--EventCalendar-fontSize-eventTitle, 0.75rem)',
  lineHeight: 1.43,
  paddingRight: theme.spacing(1.5),
  height: 'fit-content',
  '@container (max-height: 20px)': {
    fontSize: '11px',
    lineHeight: '11px'
  },
  '[data-editing] &': {
    color: 'var(--event-on-surface-selected)'
  }
}, linesClampStyles(1)));
const TimeGridEventTime = styled('time', {
  name: 'MuiEventCalendar',
  slot: 'TimeGridEventTime'
})(({
  theme
}) => ({
  color: 'var(--event-on-surface-subtle-secondary)',
  fontWeight: theme.typography.fontWeightRegular,
  fontSize: 'var(--EventCalendar-fontSize-timeText, 0.75rem)',
  lineHeight: 1.43,
  '[data-editing] &': {
    color: 'var(--event-on-surface-selected)'
  },
  '&[data-lines-clamp]': _extends({}, linesClampStyles(1), {
    paddingInlineEnd: theme.spacing(1.5)
  }),
  '@container (max-width: 50px & max-height: 50px)': {
    display: 'none'
  },
  '@container (max-height: 20px)': {
    fontSize: '11px',
    lineHeight: '11px'
  }
}));
const TimeGridEventRecurringIcon = styled(RepeatRounded, {
  name: 'MuiEventCalendar',
  slot: 'TimeGridEventRecurringIcon'
})(({
  theme
}) => ({
  position: 'absolute',
  right: 3,
  bottom: 3,
  padding: theme.spacing(0.25),
  fontSize: 'var(--EventCalendar-fontSize-recurringIcon, 1.25rem)',
  color: 'var(--event-on-surface-subtle-secondary)',
  '[data-editing] &': {
    color: 'var(--event-on-surface-selected)'
  },
  '@container (max-width: 50px)': {
    display: 'none'
  },
  '@container (max-height: 12px)': {
    fontSize: 14,
    lineHeight: 14,
    padding: 0,
    bottom: 0
  }
}));
const TimeGridEventResizeHandler = styled(CalendarGrid.TimeEventResizeHandler, {
  name: 'MuiEventCalendar',
  slot: 'TimeGridEventResizeHandler'
})({
  position: 'absolute',
  height: 4,
  left: 0,
  right: 0,
  zIndex: 3,
  cursor: 'ns-resize',
  opacity: 0,
  '*:hover > &': {
    opacity: 1
  },
  '&[data-start]': {
    top: 0
  },
  '&[data-end]': {
    bottom: 0
  }
});
export const TimeGridEvent = /*#__PURE__*/React.forwardRef(function TimeGridEvent(props, forwardedRef) {
  const {
      occurrence,
      variant,
      className
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  const {
    classes
  } = useEventCalendarStyledContext();
  const {
    isRecurring,
    isDraggable,
    isStartResizable,
    isEndResizable,
    isLessThan30Minutes,
    isBetween30and60Minutes,
    rootDataAttributes,
    rootPositionProps
  } = useTimeGridEvent(occurrence);
  const formatTime = useFormatTime();
  const content = React.useMemo(() => {
    return /*#__PURE__*/_jsxs(React.Fragment, {
      children: [isLessThan30Minutes || isBetween30and60Minutes ? /*#__PURE__*/_jsxs(TimeGridEventTitle, {
        className: classes.timeGridEventTitle,
        children: [occurrence.title, ' ', /*#__PURE__*/_jsx(TimeGridEventTime, {
          className: classes.timeGridEventTime,
          children: formatTime(occurrence.displayTimezone.start.value)
        })]
      }) : /*#__PURE__*/_jsxs(React.Fragment, {
        children: [/*#__PURE__*/_jsx(TimeGridEventTitle, {
          className: classes.timeGridEventTitle,
          children: occurrence.title
        }), /*#__PURE__*/_jsxs(TimeGridEventTime, {
          className: classes.timeGridEventTime,
          "data-lines-clamp": true,
          children: [formatTime(occurrence.displayTimezone.start.value), " -", ' ', formatTime(occurrence.displayTimezone.end.value)]
        })]
      }), isRecurring && /*#__PURE__*/_jsx(TimeGridEventRecurringIcon, {
        className: classes.timeGridEventRecurringIcon,
        "aria-hidden": "true"
      })]
    });
  }, [isBetween30and60Minutes, isLessThan30Minutes, occurrence.title, occurrence.displayTimezone.start.value, occurrence.displayTimezone.end.value, formatTime, isRecurring, classes]);
  const sharedProps = _extends({}, rootPositionProps, {
    ref: forwardedRef
  }, other, {
    className: clsx(className, occurrence.className)
  });
  if (variant === 'placeholder') {
    return /*#__PURE__*/_jsx(TimeGridEventPlaceholder, _extends({
      "aria-hidden": true
    }, rootDataAttributes, sharedProps, {
      className: clsx(classes.timeGridEventPlaceholder, sharedProps.className),
      children: content
    }));
  }
  return /*#__PURE__*/_jsxs(TimeGridEventRoot, _extends({
    isDraggable: isDraggable,
    eventId: occurrence.id,
    occurrenceKey: occurrence.key,
    renderDragPreview: parameters => /*#__PURE__*/_jsx(EventDragPreview, _extends({}, parameters))
  }, rootDataAttributes, sharedProps, {
    className: clsx(classes.timeGridEvent, sharedProps.className),
    children: [isStartResizable && /*#__PURE__*/_jsx(TimeGridEventResizeHandler, {
      className: classes.timeGridEventResizeHandler,
      side: "start"
    }), content, isEndResizable && /*#__PURE__*/_jsx(TimeGridEventResizeHandler, {
      className: classes.timeGridEventResizeHandler,
      side: "end"
    })]
  }));
});
if (process.env.NODE_ENV !== "production") TimeGridEvent.displayName = "TimeGridEvent";